import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for scroll-based reveal animations
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Intersection threshold (0-1)
 * @param {string} options.rootMargin - Root margin for intersection observer
 * @returns {Array} [ref, isVisible] - Ref to attach to element and visibility state
 */
export const useScrollReveal = (options = {}) => {
  const {
    threshold = 0.1,
    rootMargin = '0px',
    triggerOnce = true
  } = options;

  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin
      }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [threshold, rootMargin, triggerOnce]);

  return [ref, isVisible];
};

/**
 * Hook for staggered animations on multiple elements
 * @param {number} count - Number of elements
 * @param {number} delay - Delay between each element (ms)
 * @returns {Array} Array of visibility states
 */
export const useStaggeredReveal = (count, delay = 100) => {
  const [visibleItems, setVisibleItems] = useState([]);
  const [ref, isVisible] = useScrollReveal();

  useEffect(() => {
    if (isVisible && visibleItems.length < count) {
      const timer = setTimeout(() => {
        setVisibleItems(prev => [...prev, prev.length]);
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [isVisible, visibleItems, count, delay]);

  return [ref, visibleItems];
};

export default useScrollReveal;
